import React from "react";
import { useNavigate } from "react-router-dom";
import { Layers } from "lucide-react";
import LandingNavbar from "./LandingNavbar";
import FinalCtaSection from "./FinalCtaSection";
import FooterSection from "./FooterSection";

/* Six primary ways to start. The create agent picks the workflow. */
const flows = [
  {
    id: "story",
    image: "/flowImages/flow-story.jpg",
    name: "Create a Story",
    subtitle: "Stories, dialogue movies, animated series",
    explainer: "Tell a narrated story, a conversation between characters, or an animated series.",
    descColor: "#D0BCFF",
    prompts: ["Stories", "Dialogue movies", "Animated series"],
    starter: "I want to create a story: a narrated story, a dialogue movie, or an animated series.",
  },
  {
    id: "idea",
    image: "/flowImages/flow-idea.jpg",
    name: "Create from an Idea",
    subtitle: "Explainers, documentaries, news, listicles",
    explainer: "Start from a topic. Twinnee turns it into an explainer, documentary, news piece, or listicle.",
    descColor: "#62FAE3",
    prompts: ["Explainers", "Documentaries", "News", "Listicles"],
    starter: "I have an idea for an explainer, documentary, news piece, or listicle.",
  },
  {
    id: "repurpose",
    image: "/flowImages/flow-repurpose.jpg",
    name: "Repurpose a Video",
    subtitle: "Shorts, clips, highlights, B-roll",
    explainer: "Cut a longer video into Shorts, clips, highlights, or B-roll.",
    descColor: "#FF8A9B",
    prompts: ["Shorts", "Clips", "Highlights", "B-roll"],
    starter: "I want to repurpose a video into Shorts, clips, highlights, or B-roll.",
  },
  {
    id: "audio",
    image: "/flowImages/flow-audio.jpg",
    name: "Create from Audio",
    subtitle: "Music videos, lyric videos, audio stories",
    explainer: "Build a music video, a lyric video, or a story told from audio.",
    descColor: "#FFB690",
    prompts: ["Music videos", "Lyric videos", "Audio stories"],
    starter: "I want to create from audio: a music video, a lyric video, or an audio story.",
  },
  {
    id: "lesson",
    image: "/flowImages/flow-lesson.jpg",
    name: "Create a Lesson",
    subtitle: "PDFs, tutorials, courses, presentations",
    explainer: "Teach from a PDF, a tutorial, a course, or a presentation.",
    descColor: "#44E2CD",
    prompts: ["PDFs", "Tutorials", "Courses", "Presentations"],
    starter: "I want to create a lesson from a PDF, tutorial, course, or presentation.",
  },
  {
    id: "brand",
    image: "/flowImages/flow-brand.jpg",
    name: "Create for a Brand",
    subtitle: "Ads, product reviews, launches, mascots",
    explainer: "Make an ad, a product review, a launch film, or a mascot from photos.",
    descColor: "#D0BCFF",
    prompts: ["Ads", "Product reviews", "Launches", "Mascots"],
    starter: "I want to create for a brand: an ad, a product review, a launch, or a mascot.",
  },
];

const comingSoon = [
  {
    id: "podcast",
    image: "/flowImages/flow-podcast.jpg",
    name: "Create a Podcast",
    priority: "High priority",
    explainer: "Record or upload a conversation and produce a polished podcast video.",
    features: [
      "Multi-speaker editing and camera switching",
      "Speaker-focused cuts and captions",
      "Intro, outro, B-roll and sound cleanup",
      "Full episode plus promotional clips",
    ],
    bestFor: "Podcasters, interviewers, expert creators.",
  },
  {
    id: "gaming",
    image: "/flowImages/flow-gaming.jpg",
    name: "Create Gaming Content",
    priority: "High priority",
    explainer: "Turn gameplay recordings or livestreams into engaging gaming videos.",
    features: [
      "Detect kills, wins, funny moments and highlights",
      "Add zooms, memes, captions and sound effects",
      "Create highlight compilations and vertical Shorts",
      "Add facecam layouts and commentary",
    ],
    bestFor: "Gaming YouTubers, streamers, esports creators.",
  },
  {
    id: "record",
    image: "/flowImages/flow-record.jpg",
    name: "Record & Enhance",
    priority: "High priority",
    explainer: "Improve videos creators record themselves instead of generating everything with AI.",
    features: [
      "Remove silences and unwanted takes",
      "Clean audio and correct framing",
      "Add captions, B-roll, transitions and zooms",
      "Improve pacing while preserving the creator's voice",
    ],
    bestFor: "Educators, influencers, coaches and talking-head channels.",
  },
  {
    id: "presenter",
    image: "/flowImages/flow-presenter.jpg",
    name: "Create with an AI Presenter",
    explainer: "Generate presenter-led videos from scripts using an avatar or an authorized digital likeness.",
    features: [
      "Talking avatar with voice and lip synchronization",
      "News, explainers, product demos and announcements",
      "Multiple languages and presenter styles",
      "Reusable presenter profiles",
    ],
    bestFor: "Faceless channels, business creators and multilingual publishers.",
  },
  {
    id: "edit",
    image: "/flowImages/flow-edit.jpg",
    name: "Edit an Existing Video",
    explainer: "Upload footage and describe the changes in natural language.",
    features: [
      "Make this video more cinematic",
      "Remove pauses and add relevant B-roll",
      "Focus on the speaker when they talk",
      "Make the pacing faster and add subtitles",
      "Change the aspect ratio without cropping important content",
    ],
    bestFor: "Creators who already have footage and want faster editing.",
  },
  {
    id: "publish",
    image: "/flowImages/flow-publish.jpg",
    name: "Create Thumbnails & Publish",
    explainer: "Turn a finished video into a complete YouTube publishing package. This can also live inside the other workflows.",
    features: [
      "Generate thumbnail concepts",
      "Suggest titles and opening hooks",
      "Write descriptions, chapters and tags",
      "Create subtitle files and end-screen assets",
      "Prepare alternate thumbnail and title concepts for testing",
    ],
    bestFor: "Almost every YouTube creator.",
  },
  {
    id: "dub",
    image: "/flowImages/flow-dub.jpg",
    name: "Translate & Dub a Video",
    explainer: "Republish existing videos for audiences in other languages.",
    features: [
      "Translate scripts and subtitles",
      "Generate dubbed voiceovers",
      "Preserve speaker identity where supported",
      "Synchronize translated dialogue and lip movements where available",
    ],
    bestFor: "Established creators expanding into international markets.",
  },
  {
    id: "channel",
    image: "/flowImages/flow-channel.jpg",
    name: "Build My Channel",
    priority: "Strategic differentiator",
    explainer: "Configure a channel once and produce content consistently over time.",
    features: [
      "Define niche, audience and visual identity",
      "Plan a series and generate episode scripts",
      "Reuse voices, characters, intros and templates",
      "Batch-produce videos and Shorts",
      "Organize drafts, revisions and publishing schedules",
    ],
    bestFor: "Faceless channels, creator teams and production agencies.",
  },
];

const FlowCard = ({ flow }) => {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => {
        const token = typeof localStorage !== "undefined" && localStorage.getItem("storycraft_token");
        if (!token) {
          navigate("/register");
          return;
        }
        const params = new URLSearchParams();
        params.set("agent", "new");
        params.set("create", flow.starter);
        navigate(`/dashboard?${params.toString()}`);
      }}
      className="group relative rounded-[20px] overflow-hidden cursor-pointer bg-[#0a0d14] border border-white/[0.07] hover:border-[#62FAE3]/30 transition-all duration-500 hover:shadow-[0_0_40px_rgba(98,250,227,0.08)] flex flex-col justify-between"
    >
      <div>
        <div className="relative w-full h-64 overflow-hidden bg-black">
          <img
            src={flow.image}
            alt={flow.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0d14] pointer-events-none" />
        </div>

        <div className="px-5 pt-4 pb-4">
          <h3 className="text-white font-bold text-xl tracking-tight font-sora mb-2">
            {flow.name}
          </h3>
          <p
            className="text-xs sm:text-[13px] font-semibold font-sora leading-snug mb-2"
            style={{ color: flow.descColor }}
          >
            {flow.subtitle}
          </p>
          <p className="text-xs text-gray-400 font-light font-sora leading-relaxed line-clamp-3 mb-3.5">
            {flow.explainer}
          </p>
        </div>
      </div>

      {flow.prompts && flow.prompts.length > 0 && (
        <div className="px-5 pb-5 pt-1 flex flex-wrap gap-1.5 border-t border-white/[0.04]">
          {flow.prompts.map((prompt) => (
            <span
              key={prompt}
              className="text-[10px] font-medium font-sora px-2 py-0.5 rounded-md bg-white/[0.04] text-gray-300 border border-white/[0.06]"
            >
              {prompt}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const ComingSoonCard = ({ flow }) => (
  <article className="relative rounded-[20px] overflow-hidden bg-[#0a0d14] border border-white/[0.07] flex flex-col">
    <div className="relative w-full h-48 overflow-hidden bg-black">
      <img src={flow.image} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-[#0a0d14]" />
      <div className="absolute top-3 left-3 flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-bold tracking-wider font-sora px-2.5 py-1 rounded-full bg-white/10 text-white/80 border border-white/15 backdrop-blur-md">
          COMING SOON
        </span>
        {flow.priority ? (
          <span className="text-[10px] font-bold tracking-wide font-sora px-2.5 py-1 rounded-full bg-[#c4b5fd]/20 text-[#e9d5ff] border border-[#c4b5fd]/35 backdrop-blur-md">
            {flow.priority}
          </span>
        ) : null}
      </div>
    </div>
    <div className="px-5 pt-4 pb-5 flex flex-col flex-1">
      <h3 className="text-white font-bold text-xl tracking-tight font-sora mb-2">{flow.name}</h3>
      <p className="text-xs text-gray-400 font-light font-sora leading-relaxed mb-3">{flow.explainer}</p>
      <ul className="space-y-1.5 mb-4">
        {flow.features.map((feature) => (
          <li key={feature} className="text-[11px] text-gray-300 font-sora leading-snug pl-3 relative">
            <span className="absolute left-0 top-[0.45em] h-1 w-1 rounded-full bg-[#62FAE3]" />
            {feature}
          </li>
        ))}
      </ul>
      <p className="mt-auto text-[11px] text-[#9CA3AF] font-sora">
        <span className="text-[#62FAE3] font-semibold">Best for </span>
        {flow.bestFor}
      </p>
    </div>
  </article>
);

export const FlowsPage = () => {
  return (
    <div className="relative w-full min-h-screen text-white font-sora bg-[#05070a]">
      <LandingNavbar />

      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 pt-28 sm:pt-36 pb-10">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-3.5 h-3.5 text-[#62FAE3]" />
          <span className="text-[#62FAE3] text-xs font-semibold tracking-[0.18em] uppercase">
            Flows
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-white text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-2">
              Six ways to start
            </h1>
            <p className="text-gray-400 text-sm sm:text-base font-light max-w-md leading-relaxed">
              Pick what you are making. Twinnee chooses the workflow from there.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {flows.map((flow) => (
            <FlowCard key={flow.id} flow={flow} />
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 pb-16">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight mb-2">Coming soon</h2>
            <p className="text-gray-400 text-sm font-light max-w-xl leading-relaxed">
              Eight more ways to make and publish, next to the six you can start today.
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.07] border border-white/10 text-white/70 text-xs font-bold tracking-wider shrink-0">
            {comingSoon.length} COMING SOON
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {comingSoon.map((flow) => (
            <ComingSoonCard key={flow.id} flow={flow} />
          ))}
        </div>
      </div>

      <FinalCtaSection />
      <FooterSection />
    </div>
  );
};

export default FlowsPage;
